import React from 'react'
import {
  Layout,
  Showcase,
  Header,
  SectionTitle,
  Intro,
} from '../../../../components'

import './style.scss'
import data from './data.json'

export default () => {
  return (
    <>
      <Header
        site={data.site}
        slug={data.slug + '/develop/'}
        state="develop"
        title={data.title}
      />
      <Layout
        complete={true}
        site={data.site}
        slug={data.slug + '/develop/'}
        state="develop"
        title={data.title}
      >
        <Intro class="intro no-padding">
          <p>
            Buttons allow the user to move through a task by triggering single
            actions. Button classes are designed for {'<button>'} elements and{' '}
            {'<input type="button">'} elements. These classes can also style{' '}
            {'<a>'} elements as buttons, but best practice is to refrain from
            using links as buttons for semantic purposes.
          </p>
        </Intro>
        <SectionTitle>Variants</SectionTitle>
        <Showcase
          site={data.site}
          title="Primary Button"
          text="Used for the primary action in a layout, this button drives the user forward in a task. Only use one primary button per screen."
          code={code.buttonPrimary}
          js="console.log('Hello World!')"
          angular="<Button>Hi</Button>"
        >
          <button class="c-button--primary">Primary Button</button>
        </Showcase>
        <Showcase
          site={data.site}
          title="Default Button"
          text="Used for any alternate actions in a layout, this button lets the user know what they can do instead of moving forward in a task. Only use when a primary button is also present. Multiple default buttons can appear in a single screen."
          code={code.buttonDefault}
        >
          <button class="c-button">Default Button</button>
        </Showcase>
        <Showcase
          site={data.site}
          title="Subtle Button"
          text="Used instead of the default button, this button achieves a cleaner layout. Only use when a primary button is also present. Multiple subtle buttons can appear in a single screen."
          code={code.buttonSubtle}
          container={true}
        >
          <button class="c-button--subtle">Subtle Button</button>
        </Showcase>
        <hr />
        <SectionTitle>States</SectionTitle>
        <p>
          Every button variant has hover/active and focus pseudo-classes.
          Optional classes for disabled and loading are available.
        </p>
        <Showcase
          site={data.site}
          title="Disabled Button"
          text="Use the class c-button--disabled for an unavailable action. Only use when a triggering action must be completed before the button’s action becomes available. You may also disable a button without changing its style by adding a disabled attribute with the value 'disabled'."
          code={code.buttonDisabled}
        >
          <button class="c-button c-button--disabled" aria-disabled="true">
            Disabled Button
          </button>
        </Showcase>
        <Showcase
          site={data.site}
          title="Loading"
          text="Use the class c-button–-loading when making longer calls to backend services which result in a noticeable wait time."
          code={code.buttonLoading}
          variantText="The loading animation can be used with any button style, as shown below:"
        >
          <button class="c-button-loading l-margin-bottom--small">
            <span class="c-button-loading__item-1" />
            <span class="c-button-loading__item-2" />
            <span class="c-button-loading__item-3" />
          </button>
          <button class="c-button--primary-loading l-margin-bottom--small">
            <span class="c-button--primary-loading__item-1" />
            <span class="c-button--primary-loading__item-2" />
            <span class="c-button--primary-loading__item-3" />
          </button>
          <div class="showcase__component-container">
            <button class="c-button--subtle-loading">
              <span class="c-button--subtle-loading__item-1" />
              <span class="c-button--subtle-loading__item-2" />
              <span class="c-button--subtle-loading__item-3" />
            </button>
          </div>
        </Showcase>
        <hr />
        <SectionTitle>Design Environment</SectionTitle>
        <p>
          Primary buttons will always take prominent placement to the right of
          or on top of other buttons. Inline buttons should follow the Dual
          Inline Action Group pattern. Use the following layout classes to keep
          space between horizontally stacked buttons:
          <ul>
            <li>
              <strong>.l-margin-top--xlarge</strong> between a primary button
              and any component above it
            </li>
            <li>
              <strong>.l-margin-top--xsmall</strong> between a primary button
              and any button below it
            </li>
          </ul>
        </p>
        <Showcase
          site={data.site}
          title="Stacked Single Buttons"
          code={code.buttonStack}
        >
          <button class="c-button--primary l-margin-top--xlarge">
            Make Transfer
          </button>
          <button class="c-button l-margin-top--xsmall">Cancel</button>
        </Showcase>
      </Layout>
    </>
  )
}

export const code = {
  buttonDefault: `<button class="c-button">Button Text</button>`,
  buttonPrimary: `<button class="c-button--primary">Button Text</button>`,
  buttonSubtle: `<button class="c-button--subtle">Button Text</button>`,
  buttonDisabled: `<button class="c-button--disabled"role="button"aria-disabled="true">Button Text</button>`,
  buttonLoading: `<button class="c-button-loading">
  <span class="c-button-loading__item-1" />
  <span class="c-button-loading__item-2" />
  <span class="c-button-loading__item-3" />
</button>

<button class="c-button--primary-loading">
  <span class="c-button--primary-loading__item-1" />
  <span class="c-button--primary-loading__item-2" />
  <span class="c-button--primary-loading__item-3" />
</button>

<button class="c-button--subtle-loading">
  <span class="c-button--subtle-loading__item-1" />
  <span class="c-button--subtle-loading__item-2" />
  <span class="c-button--subtle-loading__item-3" />
</button>
`,
  buttonStack: `<button class="c-button--primary l-margin-top--xlarge">Button Text</button>
<button class="c-button l-margin-top--xsmall">Button Text</button>`,
  states: `<button class="c-button">Button Text</button>`,
}
